import { useCallback, useState } from 'react';
import { apiSeedHCP } from 'lib/api';
import Companies from 'lib/companies_2.json';
import MiscLocale from 'locale/en/misc.json';
import RegisterLocale from 'locale/en/register.json';
import styled from 'styled-components';
import {
  mqXLargeMin,
  mqSmallMax,
  mqTabletMax,
} from 'styled/tools/media-queries';
import {
  Fontw4s14h21,
  Fontw4s12h16,
  Fontw4s14h20,
  Fontw4s16h24,
  Fontw4s24h32,
  Fontw35s48h56,
  Fontw35s32h40,
} from 'styles/commonCss';
import Button from 'components/reusable/Button';
import Icon from 'components/global/Icon/Primary';
import { identifyAndTrackEvent } from '../../lib/analytics';
import { useHashAccountInfo } from 'hooks/use-hash-account-info';
import { hashUserInfo } from 'lib/helpers/hash-user-info';
import { validateEmail } from 'lib/utils/validate-email';

const Section = styled.section`
  display: flex;
  flex-direction: row;
  gap: 48px;
  padding: 48px;

  @media ${mqTabletMax} {
    flex-direction: column;
    gap: 24px;
    padding: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  gap: 48px;

  @media ${mqSmallMax} {
    width: 100%;
    gap: 24px;
  }

  @media ${mqTabletMax} {
    width: 100%;
    gap: 24px;
  }
`;

const Title = styled.div`
  ${Fontw35s48h56}

  @media ${mqSmallMax} {
    ${Fontw35s32h40}
  }
`;

const Description = styled.p`
  ${Fontw4s24h32}
  .lt-break {
    display: none;
  }

  @media (${mqXLargeMin}) {
    .lt-break {
      display: inline;
    }
  }

  @media ${mqSmallMax} {
    ${Fontw4s16h24}
  }
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FormContainer = styled.div`
  width: 74%;
  justify-content: center;
  border-radius: var(--spacing-sm, 4px);
  border: 1px solid var(--outline-neutral);
  display: flex;
  flex-direction: column;
  padding: 24px;

  @media ${mqSmallMax} {
    border: none;
    padding: 0px;
    margin-left: 0px;
    width: 100%;
  }
`;

const FormTitle = styled.div`
  ${Fontw4s24h32}
  text-align: center;
  margin-bottom: 12px;
`;

const FormText = styled.div`
  ${Fontw4s14h20}
  text-align: center;
  max-width: 250px;
  margin: auto;
  margin-bottom: 12px;
`;

const Input = styled.input`
  ${Fontw4s14h21}
  color: var(--text-primary);
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid var(--outline-neutral);
  width: 100%;
  margin-bottom: 16px;
  padding: 10px 0;

  &::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: var(--text-primary);
    opacity: 1; /* Firefox */
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: var(--text-primary);
  }

  &::-ms-input-placeholder {
    /* Microsoft Edge */
    color: var(--text-primary);
  }
`;

const SelectContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--outline-neutral);
  margin-bottom: 24px;
  padding: 3px 0;
  color: var(--fill-brand);

  @media ${mqSmallMax} {
    margin-bottom: 36px;
  }
`;

const Select = styled.select`
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: var(--text-primary);
  border: none;
  width: 100%;
`;

const ConsentText = styled.div`
  ${Fontw4s12h16}
  color: var(--text-secondary);
  margin-top: 20px;
`;

const FormHcpNew = () => {
  const Default_State = {
    email: '',
    First_Name: '',
    Last_Name: '',
    hcp_type: '',
    loading: false,
    errorCount: 0,
    errors: null,
    show: true,
    hasError: false,
    hasAlert: {
      msg: null,
      type: null,
      show: false,
    },
    verified: false,
  };
  const [formData, setFormData] = useState(Default_State);
  const [submitted, setSubmitted] = useState(false);
  const { hashedAccountInfo } = useHashAccountInfo();
  async function checkErrors() {
    const { email } = formData;
    const errors = {
      email: null,
      First_Name: null,
      Last_Name: null,
      hcp_type: null,
    };

    // hcp_type
    if (formData.hcp_type === '') {
      errors.hcp_type = MiscLocale.error_job_required;
    }
    // at least 2 characters and letters only
    const nameRegex = /^[a-zA-Z]{2,}$/;
    if (!nameRegex.test(formData.First_Name)) {
      errors.First_Name = 'Please enter a valid first name';
    }
    if (!nameRegex.test(formData.Last_Name)) {
      errors.Last_Name = 'Please enter a valid last name';
    }
    if (!validateEmail(email)) {
      errors.email = RegisterLocale.error_email;
    }

    return errors;
  }

  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [id]: value }));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const { email, First_Name, Last_Name, hcp_type, hasAlert, loading } =
      formData;
    hasAlert.show = false;
    setFormData({ ...formData, hasAlert });

    const errors: any = await checkErrors();

    const formValid = Object.values(errors).every((x) => x === null);

    if (!formValid) {
      const alert = {
        msg:
          errors.email ||
          errors.First_Name ||
          errors.Last_Name ||
          errors.hcp_type,
        type: 'error',
        show: true,
      };
      setFormData({
        ...formData,
        hasAlert: alert,
        errorCount: Object.keys(errors).length,
      });

      return;
    }

    if (!loading && formValid) {
      const payload = await apiSeedHCP({
        First_Name,
        Last_Name,
        email,
        hcp_type,
        'Inquiry Date': new Date().toLocaleDateString('en-US'),
        'Lead Source': 'LP',
      });

      const newAlert = {
        msg: payload.data.msg,
        type: payload.status === 200 ? 'success' : 'error',
        show: true,
      };

      if (payload.status === 200) {
        setSubmitted(true);
        setFormData({ ...formData, hasAlert: newAlert, loading: false });
        const hashedUserInfo = await hashUserInfo({
          firstName: First_Name,
          lastName: Last_Name,
          email: email,
        });
        identifyAndTrackEvent(
          null,
          {
            name: `${First_Name} ${Last_Name}`,
            firstName: First_Name,
            lastName: Last_Name,
            email,
            hcpType: hcp_type,
          },
          'HCP Form Submitted',
          {
            email,
            First_Name,
            Last_Name,
            hcp_type,
            hashedUserInfo,
            hashedAccountInfo,
          },
        );
      } else {
        setFormData((data) => ({
          ...data,
          errorCount: formData.errorCount + 1,
          hasAlert: newAlert,
          loading: false,
        }));
      }
    }
  }

  return (
    <Section id='form-hcp'>
      <LeftColumn>
        <Title>
          Real clinicians,
          <br /> here to support.
        </Title>
        <Description>
          Our Practitioner Team is here ready to help you&nbsp;
          <br className='lt-break' />
          integrate DS-01® into your practice.
        </Description>
      </LeftColumn>
      <RightColumn>
        <FormContainer>
          <FormTitle>Ready to learn more?</FormTitle>
          <FormText>
            Access practitioner resources, and your complimentary DS-01®
            product trial.
          </FormText>
          <form noValidate onSubmit={handleSubmit} className='form'>
            <Input
              id='email'
              required
              aria-label='Email address'
              role='textbox'
              placeholder='Email'
              onChange={handleChange}
            />
            <Input
              id='First_Name'
              required
              aria-label='First Name'
              role='textbox'
              placeholder='First Name'
              onChange={handleChange}
            />
            <Input
              id='Last_Name'
              required
              aria-label='Last Name'
              role='textbox'
              placeholder='Last Name'
              onChange={handleChange}
            />
            <SelectContainer>
              <Select
                id='hcp_type'
                aria-label='Healthcare Certification'
                defaultValue='no'
                onChange={handleChange}
              >
                <option key='' value=''>
                  Healthcare Certification
                </option>
                {Companies.map((v) => (
                  <option key={v.name} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
              <Icon name='chevron-down' size='md' />
            </SelectContainer>
            <Button
              aria-label='Submit'
              role='button'
              type='submit'
              value='Submit'
              theme={{ height: '60px' }}
            >
              Connect with Our Practitioner Team
            </Button>
            {!submitted && (
              <ConsentText>
                By signing up, you consent to receive healthcare practitioner
                communications from Seed.
              </ConsentText>
            )}
          </form>
          <p
            style={{
              color: submitted ? '#1C3A13' : 'red',
              background: submitted ? '#E9F0CA' : null,
              height: '60px',
              width: '342px',
              padding: '8px',
              gap: '10px',
              maxWidth: '100%',
            }}
          >
            {formData.hasAlert.msg}
          </p>
        </FormContainer>
      </RightColumn>
    </Section>
  );
};

export default FormHcpNew;
